<!-- ============================================
     MODO MODAL: Wrapper con overlay y header
     ============================================ -->
<div *ngIf="isModal" class="modal-overlay" (click)="onCloseModal()">
  <div class="modal-container" (click)="$event.stopPropagation()">
    <!-- HEADER MODAL -->
    <div class="modal-header">
      <div class="header-info">
        <h2>Recetas Médicas</h2>
        <p>{{ patient?.full_name || patient?.nombreCompleto || 'Paciente' }}</p>
      </div>
      <button class="btn-close" (click)="onCloseModal()">
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <!-- CONTENT MODAL -->
    <div class="modal-content">
      <ng-container *ngTemplateOutlet="contenidoIndicaciones"></ng-container>
    </div>
  </div>
</div>

<!-- ============================================
     MODO NORMAL: Wrapper como componente card
     ============================================ -->
<div *ngIf="!isModal" class="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 flex flex-col gap-4 mb-6">
  <ng-container *ngTemplateOutlet="contenidoIndicaciones"></ng-container>
</div>

<!-- ============================================
     TEMPLATE COMPARTIDO: Contenido de indicaciones
     ============================================ -->
<ng-template #contenidoIndicaciones>
  <div class="flex items-center justify-between mb-2">
    <div class="flex items-center gap-3">
      <h3 class="text-2xl font-bold text-gray-800 tracking-tight">Indicaciones médicas (Recetas)</h3>
      <span *ngIf="indicacionesCollapsed" class="text-sm text-slate-500">(minimizado)</span>
    </div>
    <div class="flex items-center gap-2">
      <button
        type="button"
        class="p-2.5 rounded-full bg-[#AE91E3] text-white hover:bg-[#9370DB] transition"
        (click)="iniciarNuevaReceta()"
        [disabled]="isLocked"
        title="Nueva receta"
      >
        <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
        </svg>
      </button>
      <button
        type="button"
        class="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border bg-white text-sm text-slate-700 hover:bg-gray-50 transition"
        (click)="toggleIndicacionesCollapsed()"
      >
        <svg *ngIf="!indicacionesCollapsed" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M5 15l7-7 7 7" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        <svg *ngIf="indicacionesCollapsed" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M19 9l-7 7-7-7" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>
    </div>
  </div>

  <ng-container *ngIf="!indicacionesCollapsed">
    <!-- Tabs de indicaciones -->
    <div class="flex items-center gap-2 border-b pb-2">
      <button
        *ngFor="let item of visibleIndicaciones; trackBy: trackByIndex"
        type="button"
        class="px-3 py-1.5 rounded-t text-sm transition cursor-pointer flex items-center gap-1"
        [class.bg-[#AE91E3]]="activeIndicacionIndex === item.index && !item.item.esHistorico"
        [class.text-white]="activeIndicacionIndex === item.index && !item.item.esHistorico"
        [class.bg-amber-100]="item.item.esHistorico && activeIndicacionIndex !== item.index"
        [class.bg-amber-200]="item.item.esHistorico && activeIndicacionIndex === item.index"
        [class.text-amber-800]="item.item.esHistorico"
        [class.bg-gray-100]="activeIndicacionIndex !== item.index && !item.item.esHistorico"
        [class.text-gray-700]="activeIndicacionIndex !== item.index && !item.item.esHistorico"
        (click)="selectTab(item.index)"
      >
        <svg *ngIf="item.item.esHistorico" class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        {{ item.item.nombre }}
      </button>
      <button
        *ngIf="overflowCount > 0"
        type="button"
        class="px-3 py-1.5 rounded bg-gray-200 text-gray-700 text-sm hover:bg-gray-300"
        (click)="openOverflowIndicaciones()"
      >
        +{{ overflowCount }} más
      </button>
    </div>

    <!-- Mensaje cuando no hay receta de la nota actual seleccionada -->
    <div *ngIf="!recetaActualNoHistorica" class="flex items-center gap-3 p-4 rounded-lg bg-gray-50 border border-gray-200 mb-4">
      <svg class="w-6 h-6 text-gray-400 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <div class="flex-1">
        <p class="text-sm font-medium text-gray-600">No se han creado recetas para esta nota médica</p>
        <p class="text-xs text-gray-500 mt-1">
          Haz clic en el botón "+" para crear una nueva receta<span *ngIf="tieneHistorial">, o selecciona una del historial para ver su contenido</span>.
        </p>
      </div>
    </div>

    <!-- Contenido de la indicación activa -->
    <div *ngIf="activeIndicacionIndex !== null && recetaActual" class="space-y-4">
      <!-- Indicador de histórico -->
      <div *ngIf="recetaActual?.esHistorico" class="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
        <svg class="w-5 h-5 text-amber-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span class="text-sm text-amber-800 font-medium">Esta es una receta histórica de otra nota médica (solo lectura)</span>
      </div>

      <!-- Header con titulo y botones secundarios (Plantillas, Imprimir, WhatsApp) -->
      <div class="flex items-center justify-between">
        <h4 class="font-semibold text-gray-800">
          {{ recetaActual.nombre }}
        </h4>
        <div class="flex gap-2">
          <!-- Botón Reemitir (solo si SÍ está finalizada) -->
          <button
            *ngIf="recetaActual?.finalizada"
            type="button"
            class="px-3 py-1.5 rounded-full bg-[#AE91E3] text-white text-xs hover:bg-[#9370DB] transition"
            (click)="reemitirReceta()"
            [disabled]="isLocked"
          >
            Reemitir
          </button>
          <!-- Botón Plantillas -->
          <button
            type="button"
            class="px-3 py-1.5 rounded-full bg-[#AE91E3] text-white text-xs hover:bg-[#9370DB] transition"
            (click)="abrirModalPlantillas()"
            [disabled]="isLocked"
            title="Plantillas"
          >
            Plantillas
          </button>
          <!-- Botón Imprimir - Solo ícono -->
          <button
            type="button"
            class="p-2 rounded-full bg-[#AE91E3] text-white hover:bg-[#9370DB] transition"
            (click)="imprimirReceta()"
            title="Imprimir"
          >
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"
              />
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 14h12v8H6z" />
            </svg>
          </button>
          <!-- Botón WhatsApp - Solo ícono CORRECTO -->
          <button
            type="button"
            class="p-2 rounded-full bg-[#AE91E3] text-white hover:bg-[#9370DB] transition"
            (click)="enviarPorWhatsapp()"
            title="WhatsApp"
          >
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path
                d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"
              />
            </svg>
          </button>
        </div>
      </div>

      <!-- VISTA EDITABLE: Mostrar cuando NO está finalizada -->
      <ng-container *ngIf="!recetaActual?.finalizada">
        <!-- Indicador de modo de entrada -->
        <div *ngIf="recetaActual.modo_entrada" class="flex items-center justify-between mb-3 px-3 py-2 bg-gray-100 rounded-lg">
          <span class="text-sm text-gray-600">
            Modo:
            <span class="font-medium text-[#AE91E3]">
              {{ recetaActual.modo_entrada === 'formulario' ? 'Formulario' : 'Texto libre' }}
            </span>
          </span>
          <button
            type="button"
            class="text-xs text-gray-500 hover:text-[#AE91E3] transition"
            (click)="cambiarModoEntrada()"
            [disabled]="isLocked"
          >
            Cambiar modo
          </button>
        </div>

        <!-- MODO TEXTO ENRIQUECIDO: Editor Quill -->
        <ng-container *ngIf="recetaActual?.modo_entrada === 'texto'">
          <div class="border rounded-lg overflow-hidden">
            <quill-editor
              [(ngModel)]="recetaActual!.contenido_html"
              [modules]="quillModules"
              [placeholder]="'Escribe las indicaciones médicas...'"
              [readOnly]="isLocked"
              (onContentChanged)="onContenidoHtmlChanged($event)"
              class="bg-white"
              [styles]="{ 'min-height': '200px' }"
              spellcheck="true"
            ></quill-editor>
          </div>
        </ng-container>

        <!-- MODO FORMULARIO: Lista de medicamentos -->
        <ng-container *ngIf="recetaActual?.modo_entrada !== 'texto'">
          <!-- Lista de medicamentos -->
          <div *ngIf="medicamentosActivos.length === 0 && !recetaActual?.modo_entrada" class="text-center py-8 text-gray-500">
            No hay medicamentos en esta receta. Haz clic en "+ Medicamento" para agregar uno.
          </div>

          <div *ngIf="medicamentosActivos.length === 0 && recetaActual?.modo_entrada === 'formulario'" class="text-center py-8 text-gray-500">
            No hay medicamentos en esta receta. Haz clic en "+ Medicamento" para agregar uno.
          </div>

          <div *ngIf="medicamentosActivos.length > 0" class="space-y-3">
          <div *ngFor="let med of medicamentosActivos; let mi = index" class="border rounded-lg p-4 bg-gray-50">
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <div class="flex flex-col">
                <label class="text-xs font-medium text-gray-600 mb-1">Nombre</label>
                <input
                  type="text"
                  class="w-full px-3 py-2 text-sm rounded border focus:outline-none focus:ring-2 focus:ring-[#AE91E3]"
                  placeholder="Nombre del medicamento"
                  [(ngModel)]="med.nombre"
                  [disabled]="isLocked"
                  (ngModelChange)="emitirCambios()"
                  spellcheck="true"
                />
              </div>
              <div class="flex flex-col">
                <label class="text-xs font-medium text-gray-600 mb-1">Dosis</label>
                <input
                  type="text"
                  class="w-full px-3 py-2 text-sm rounded border focus:outline-none focus:ring-2 focus:ring-[#AE91E3]"
                  placeholder="500mg"
                  [(ngModel)]="med.dosis"
                  [disabled]="isLocked"
                  (ngModelChange)="emitirCambios()"
                />
              </div>
              <div class="flex flex-col">
                <label class="text-xs font-medium text-gray-600 mb-1">Frecuencia</label>
                <input
                  type="text"
                  class="w-full px-3 py-2 text-sm rounded border focus:outline-none focus:ring-2 focus:ring-[#AE91E3]"
                  placeholder="Cada 8 horas"
                  [(ngModel)]="med.frecuencia"
                  [disabled]="isLocked"
                  (ngModelChange)="emitirCambios()"
                />
              </div>
              <div class="flex flex-col">
                <label class="text-xs font-medium text-gray-600 mb-1">Vía</label>
                <input
                  type="text"
                  class="w-full px-3 py-2 text-sm rounded border focus:outline-none focus:ring-2 focus:ring-[#AE91E3]"
                  placeholder="Oral"
                  [(ngModel)]="med.via"
                  [disabled]="isLocked"
                  (ngModelChange)="emitirCambios()"
                />
              </div>
              <div class="flex flex-col">
                <label class="text-xs font-medium text-gray-600 mb-1">Duración</label>
                <input
                  type="text"
                  class="w-full px-3 py-2 text-sm rounded border focus:outline-none focus:ring-2 focus:ring-[#AE91E3]"
                  placeholder="7 días"
                  [(ngModel)]="med.duracion"
                  [disabled]="isLocked"
                  (ngModelChange)="emitirCambios()"
                />
              </div>
              <div class="flex flex-col">
                <label class="text-xs font-medium text-gray-600 mb-1">Indicaciones</label>
                <input
                  type="text"
                  class="w-full px-3 py-2 text-sm rounded border focus:outline-none focus:ring-2 focus:ring-[#AE91E3]"
                  placeholder="Antes de comidas"
                  [(ngModel)]="med.indicaciones"
                  [disabled]="isLocked"
                  (ngModelChange)="emitirCambios()"
                  spellcheck="true"
                />
              </div>
            </div>
            <div class="flex justify-end mt-2">
              <button
                *ngIf="!isLocked"
                type="button"
                class="px-3 py-1.5 rounded-full bg-red-500 text-white text-xs hover:bg-red-600 transition"
                (click)="eliminarMedicamento(mi)"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>

        <!-- Barra de acciones flotante para modo formulario -->
        <div class="sticky bottom-0 bg-white border-t border-gray-200 py-3 -mx-6 px-6 mt-4 flex items-center justify-between shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
          <div class="flex items-center gap-2">
            <button
              type="button"
              class="px-4 py-2 rounded-full bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition flex items-center gap-2"
              (click)="eliminarRecetaActual()"
              [disabled]="isLocked"
              title="Eliminar receta"
            >
              <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Eliminar
            </button>
            <span class="text-sm text-gray-600 ml-2">
              {{ medicamentosActivos.length }} medicamento(s)
            </span>
          </div>
          <div class="flex gap-2">
            <button
              type="button"
              class="px-4 py-2 rounded-full bg-[#AE91E3] text-white text-sm font-medium hover:bg-[#9370DB] transition flex items-center gap-2"
              (click)="agregarMedicamento()"
              [disabled]="isLocked"
            >
              <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              Medicamento
            </button>
            <button
              type="button"
              class="px-4 py-2 rounded-full bg-[#AE91E3] text-white text-sm font-medium hover:bg-[#9370DB] transition flex items-center gap-2"
              (click)="guardarReceta()"
              [disabled]="isLocked"
            >
              <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              Guardar
            </button>
          </div>
        </div>
        </ng-container>

        <!-- Barra de acciones para modo texto enriquecido -->
        <div *ngIf="recetaActual?.modo_entrada === 'texto'" class="sticky bottom-0 bg-white border-t border-gray-200 py-3 -mx-6 px-6 mt-4 flex items-center justify-between shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
          <button
            type="button"
            class="px-4 py-2 rounded-full bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition flex items-center gap-2"
            (click)="eliminarRecetaActual()"
            [disabled]="isLocked"
            title="Eliminar receta"
          >
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Eliminar
          </button>
          <div class="flex gap-2">
            <button
              type="button"
              class="px-4 py-2 rounded-full bg-[#AE91E3] text-white text-sm font-medium hover:bg-[#9370DB] transition flex items-center gap-2"
              (click)="guardarReceta()"
              [disabled]="isLocked"
            >
              <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              Guardar
            </button>
          </div>
        </div>

        <!-- Notas -->
        <div class="flex flex-col">
          <label class="text-sm font-medium text-gray-700 mb-1">Notas adicionales</label>
          <textarea
            class="w-full px-3 py-2 text-sm rounded border focus:outline-none focus:ring-2 focus:ring-[#AE91E3]"
            rows="3"
            placeholder="Notas o instrucciones adicionales..."
            [(ngModel)]="recetaActual!.notas"
            [disabled]="isLocked"
            (ngModelChange)="emitirCambios()"
            spellcheck="true"
          ></textarea>
        </div>
      </ng-container>

      <!-- VISTA PREVIEW: Mostrar cuando SÍ está finalizada -->
      <ng-container *ngIf="recetaActual?.finalizada">
        <!-- PREVIEW MODO TEXTO ENRIQUECIDO -->
        <div *ngIf="recetaActual?.modo_entrada === 'texto'" class="border rounded-lg p-4 bg-white">
          <div class="flex items-center gap-2 mb-3 pb-2 border-b">
            <svg class="w-5 h-5 text-[#AE91E3]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span class="text-sm font-medium text-gray-700">Indicaciones (Texto libre)</span>
          </div>
          <div
            class="prose prose-sm max-w-none text-gray-700"
            [innerHTML]="recetaActual.contenido_html"
          ></div>
          <div *ngIf="!recetaActual?.contenido_html" class="text-center py-4 text-gray-500 italic">
            Sin contenido
          </div>
        </div>

        <!-- PREVIEW MODO FORMULARIO -->
        <div *ngIf="recetaActual?.modo_entrada !== 'texto'" class="border rounded-lg overflow-hidden bg-gray-50">
          <!-- Header de tabla -->
          <div class="bg-white border-b px-4 py-3">
            <div class="grid grid-cols-12 gap-2 text-xs font-semibold text-gray-600 uppercase tracking-wide">
              <div class="col-span-4">Medicamento</div>
              <div class="col-span-2">Dosis</div>
              <div class="col-span-2">Frecuencia</div>
              <div class="col-span-2">Vía</div>
              <div class="col-span-2">Duración</div>
            </div>
          </div>

          <!-- Lista de medicamentos con scroll -->
          <div class="max-h-[300px] overflow-y-auto custom-scrollbar">
            <div *ngIf="medicamentosActivos.length === 0" class="text-center py-8 text-gray-500">
              No hay medicamentos en esta receta.
            </div>

            <div *ngFor="let med of medicamentosActivos; let mi = index" class="border-b last:border-b-0">
              <div class="px-4 py-3 hover:bg-gray-100 transition">
                <!-- Fila principal -->
                <div class="grid grid-cols-12 gap-2 text-sm">
                  <div class="col-span-4 font-medium text-gray-800">
                    <span class="inline-block w-6 text-[#AE91E3] font-semibold">{{ mi + 1 }}.</span>
                    {{ med.nombre || 'Sin nombre' }}
                  </div>
                  <div class="col-span-2 text-gray-600">{{ med.dosis || '-' }}</div>
                  <div class="col-span-2 text-gray-600">{{ med.frecuencia || '-' }}</div>
                  <div class="col-span-2 text-gray-600">{{ med.via || '-' }}</div>
                  <div class="col-span-2 text-gray-600">{{ med.duracion || '-' }}</div>
                </div>

                <!-- Indicaciones (si existen) -->
                <div *ngIf="med.indicaciones" class="mt-2 ml-6 text-sm text-gray-600 flex items-start gap-2">
                  <svg
                    class="w-4 h-4 text-[#AE91E3] flex-shrink-0 mt-0.5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>{{ med.indicaciones }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Indicador de scroll si hay muchos medicamentos -->
          <div *ngIf="medicamentosActivos.length > 4" class="bg-white border-t px-4 py-2 text-center">
            <span class="text-xs text-gray-500 flex items-center justify-center gap-1">
              <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
              Desplázate para ver más medicamentos
            </span>
          </div>
        </div>

        <!-- Notas adicionales -->
        <div *ngIf="recetaActual?.notas" class="border rounded-lg p-4 bg-white">
          <div class="flex items-start gap-2">
            <svg
              class="w-5 h-5 text-[#AE91E3] flex-shrink-0 mt-0.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <div class="flex-1">
              <h5 class="text-sm font-semibold text-gray-700 mb-1">Notas adicionales</h5>
              <p class="text-sm text-gray-600 whitespace-pre-wrap">
                {{ recetaActual.notas }}
              </p>
            </div>
          </div>
        </div>
      </ng-container>
    </div>
  </ng-container>
</ng-template>

<!-- Modal overflow -->
<div *ngIf="overflowIndicacionesOpen"
     class="overflow-modal-wrapper"
     style="position: fixed !important; top: 0 !important; left: 0 !important; right: 0 !important; bottom: 0 !important; z-index: 999999 !important; display: grid !important; place-items: center !important; padding: 1rem !important; background: rgba(0,0,0,0.3) !important;">
  <div style="position: absolute; inset: 0;" (click)="closeOverflowIndicaciones()"></div>
  <div style="position: relative; z-index: 10; width: 100%; max-width: 36rem; background: white !important; border-radius: 1rem; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25); padding: 1rem;">
    <header class="flex items-center justify-between mb-3">
      <h3 class="text-lg font-semibold">Todas las indicaciones ({{ indicacionesMedicas.length }})</h3>
      <button class="text-sm px-3 py-1 rounded bg-white border" (click)="closeOverflowIndicaciones()">Cerrar</button>
    </header>
    <div class="max-h-[60vh] overflow-y-auto space-y-2">
      <div
        *ngFor="let p of overflowIndicaciones"
        class="w-full text-left p-3 rounded-lg border hover:bg-slate-50 flex items-center justify-between cursor-pointer"
        (click)="selectIndicacionFromOverflow(p.index)"
      >
        <div class="flex-1 min-w-0">
          <div class="font-medium text-sm truncate">{{ p.item.nombre }}</div>
          <div class="text-xs text-slate-500">{{ p.item.tipo }}</div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Modal de Plantillas - z-index mayor que modal-overlay -->
<div *ngIf="modalPlantillasAbierto" class="fixed inset-0 z-[100002] grid place-items-center p-4">
  <div class="fixed inset-0 bg-black/30" (click)="cerrarModalPlantillas()"></div>
  <div class="relative z-10 w-full max-w-2xl rounded-2xl bg-white shadow-lg p-6">
    <header class="flex items-center justify-between mb-4">
      <h3 class="text-xl font-semibold text-gray-800">Plantillas de Recetas</h3>
      <button
        class="text-sm px-3 py-1.5 rounded-full border bg-white hover:bg-gray-50 transition"
        (click)="cerrarModalPlantillas()"
      >
        Cerrar
      </button>
    </header>

    <!-- Botón nueva plantilla -->
    <div class="mb-4">
      <button
        type="button"
        class="px-4 py-2 rounded-full bg-[#AE91E3] text-white text-sm hover:bg-[#9370DB] transition"
        (click)="iniciarCreacionPlantilla()"
        [disabled]="medicamentosActivos.length === 0"
      >
        + Nueva plantilla desde receta actual
      </button>
      <p *ngIf="medicamentosActivos.length === 0" class="text-xs text-gray-500 mt-1">
        Agrega medicamentos a la receta actual para crear una plantilla
      </p>
    </div>

    <!-- Lista de plantillas -->
    <div class="border-t pt-4">
      <div *ngIf="plantillasRecetas.length === 0" class="text-center py-8 text-gray-500">
        No hay plantillas guardadas. Crea una nueva plantilla desde una receta con medicamentos.
      </div>

      <div class="space-y-3 max-h-[400px] overflow-y-auto custom-scrollbar">
        <div
          *ngFor="let plantilla of plantillasRecetas"
          class="border rounded-lg p-4 bg-gray-50 hover:bg-gray-100 transition"
        >
          <div class="flex items-start justify-between gap-4">
            <div class="flex-1">
              <h4 class="font-semibold text-gray-800 flex items-center gap-2">
                <svg
                  class="w-4 h-4 text-[#AE91E3]"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                {{ plantilla.nombre }}
              </h4>
              <p *ngIf="plantilla.descripcion" class="text-sm text-gray-600 mt-1">{{ plantilla.descripcion }}</p>
              <p class="text-xs text-gray-500 mt-1">{{ plantilla.medicamentos.length }} medicamento(s)</p>
            </div>
            <div class="flex gap-2">
              <button
                type="button"
                class="px-3 py-1.5 rounded-full bg-[#AE91E3] text-white text-xs hover:bg-[#9370DB] transition"
                (click)="aplicarPlantilla(plantilla.id)"
              >
                Aplicar
              </button>
              <button
                type="button"
                class="p-1.5 rounded-full text-gray-600 hover:bg-gray-200 transition"
                (click)="editarPlantilla(plantilla.id)"
                title="Editar"
              >
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
              </button>
              <button
                type="button"
                class="p-1.5 rounded-full text-red-600 hover:bg-red-50 transition"
                (click)="eliminarPlantilla(plantilla.id)"
                title="Eliminar"
              >
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Modal crear/editar plantilla - z-index mayor que otros modales -->
<div *ngIf="modalFormPlantillaAbierto" class="fixed inset-0 z-[100003] grid place-items-center p-4">
  <div class="fixed inset-0 bg-black/30" (click)="cerrarFormPlantilla()"></div>
  <div class="relative z-10 w-full max-w-md rounded-2xl bg-white shadow-lg p-6">
    <h3 class="text-lg font-semibold text-gray-800 mb-4">
      {{ plantillaEnEdicion ? 'Editar Plantilla' : 'Nueva Plantilla' }}
    </h3>

    <div class="space-y-4">
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Nombre de la plantilla *</label>
        <input
          type="text"
          class="w-full px-3 py-2 text-sm rounded border focus:outline-none focus:ring-2 focus:ring-[#AE91E3]"
          placeholder="Ej: Antibióticos comunes"
          [(ngModel)]="formPlantilla.nombre"
          spellcheck="true"
        />
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Descripción (opcional)</label>
        <textarea
          class="w-full px-3 py-2 text-sm rounded border focus:outline-none focus:ring-2 focus:ring-[#AE91E3]"
          rows="3"
          placeholder="Descripción de la plantilla..."
          [(ngModel)]="formPlantilla.descripcion"
          spellcheck="true"
        ></textarea>
      </div>

      <div class="flex gap-2 justify-end">
        <button
          type="button"
          class="px-4 py-2 rounded-full border bg-white text-sm hover:bg-gray-50 transition"
          (click)="cerrarFormPlantilla()"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="px-4 py-2 rounded-full bg-[#AE91E3] text-white text-sm hover:bg-[#9370DB] transition"
          (click)="guardarFormPlantilla()"
          [disabled]="!formPlantilla.nombre.trim()"
        >
          {{ plantillaEnEdicion ? 'Actualizar' : 'Crear' }}
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Modal de confirmación de cambio de modo -->
<div *ngIf="modalConfirmarCambioModoAbierto" class="fixed inset-0 z-[100005] grid place-items-center p-4">
  <div class="fixed inset-0 bg-black/30" (click)="cerrarModalConfirmarCambioModo()"></div>
  <div class="relative z-10 w-full max-w-sm rounded-2xl bg-white shadow-lg p-5">
    <div class="flex justify-center mb-3">
      <div class="p-3 bg-amber-100 rounded-full">
        <svg class="w-6 h-6 text-amber-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
    </div>
    <h3 class="text-base font-semibold text-gray-800 mb-2 text-center">¿Cambiar modo de entrada?</h3>
    <p class="text-sm text-gray-500 mb-4 text-center">Se perderá el contenido actual de esta receta.</p>

    <div class="flex gap-2">
      <button
        type="button"
        class="flex-1 px-4 py-2 rounded-full border bg-white text-sm text-gray-600 hover:bg-gray-50 transition"
        (click)="cerrarModalConfirmarCambioModo()"
      >
        Cancelar
      </button>
      <button
        type="button"
        class="flex-1 px-4 py-2 rounded-full bg-amber-500 text-white text-sm hover:bg-amber-600 transition"
        (click)="confirmarCambioModo()"
      >
        Sí, cambiar
      </button>
    </div>
  </div>
</div>

<!-- Modal de selección de modo de entrada -->
<div *ngIf="modalSeleccionModoAbierto" class="fixed inset-0 z-[100004] grid place-items-center p-4">
  <div class="fixed inset-0 bg-black/30" (click)="cerrarModalSeleccionModo()"></div>
  <div class="relative z-10 w-full max-w-md rounded-2xl bg-white shadow-lg p-5">
    <h3 class="text-base font-semibold text-gray-800 mb-4 text-center">¿Cómo deseas ingresar las indicaciones?</h3>

    <div class="grid grid-cols-2 gap-3">
      <!-- Opción Formulario -->
      <button
        type="button"
        class="p-3 rounded-xl border-2 border-gray-200 hover:border-[#AE91E3] hover:bg-purple-50 transition flex flex-col items-center gap-2 text-center"
        (click)="seleccionarModoFormulario()"
      >
        <div class="p-2 bg-[#AE91E3]/10 rounded-lg">
          <svg class="w-5 h-5 text-[#AE91E3]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        </div>
        <div>
          <h4 class="font-semibold text-gray-800 text-sm">Formulario</h4>
          <p class="text-xs text-gray-500 mt-0.5">Campos estructurados</p>
        </div>
      </button>

      <!-- Opción Texto Enriquecido -->
      <button
        type="button"
        class="p-3 rounded-xl border-2 border-gray-200 hover:border-[#AE91E3] hover:bg-purple-50 transition flex flex-col items-center gap-2 text-center"
        (click)="seleccionarModoTexto()"
      >
        <div class="p-2 bg-[#AE91E3]/10 rounded-lg">
          <svg class="w-5 h-5 text-[#AE91E3]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </div>
        <div>
          <h4 class="font-semibold text-gray-800 text-sm">Texto libre</h4>
          <p class="text-xs text-gray-500 mt-0.5">Formato libre</p>
        </div>
      </button>
    </div>

    <button
      type="button"
      class="w-full mt-4 px-4 py-1.5 rounded-full border bg-white text-xs text-gray-600 hover:bg-gray-50 transition"
      (click)="cerrarModalSeleccionModo()"
    >
      Cancelar
    </button>
  </div>
</div>

<!-- Modal de confirmación de eliminación -->
<div *ngIf="modalConfirmarEliminacionAbierto" class="fixed inset-0 z-[100006] grid place-items-center p-4">
  <div class="fixed inset-0 bg-black/30" (click)="cerrarModalEliminar()"></div>
  <div class="relative z-10 w-full max-w-sm rounded-2xl bg-white shadow-lg p-6">
    <div class="flex justify-center mb-4">
      <div class="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
        <svg class="w-8 h-8 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </div>
    </div>
    <h3 class="text-lg font-semibold text-gray-900 text-center mb-2">Eliminar receta</h3>
    <p class="text-sm text-gray-600 text-center mb-6">
      ¿Estás seguro de que deseas eliminar esta receta? Esta acción no se puede deshacer.
    </p>
    <div class="flex gap-3">
      <button
        type="button"
        class="flex-1 px-4 py-2.5 rounded-full border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 transition"
        (click)="cerrarModalEliminar()"
      >
        Cancelar
      </button>
      <button
        type="button"
        class="flex-1 px-4 py-2.5 rounded-full bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition"
        (click)="confirmarEliminacion()"
      >
        Eliminar
      </button>
    </div>
  </div>
</div>
